import '../ExportReports.scss';

import * as React from 'react';

import { ActivityType, ReportTypes } from '@showbie-socrative/socrative-utils';

import { translate } from '~l10n/client';
import { IconButton } from '~shared/components/buttons';
import { Dialog, DialogProps } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { IconChevron } from '~shared/components/icons/standard';
import { getReportSettings } from '~teacher/helpers/ReportSettings';

import * as reportsApi from '../../../../api/reports/reportsApi';
import { AssessmentsExportReportsAnswerKey } from './AssessmentsExportReportsAnswerKey';
import { AssessmentsExportReportsCompleteResults } from './AssessmentsExportReportsCompleteResults';
import { AssessmentsExportReportsIndividual } from './AssessmentsExportReportsIndividual';
import { AssessmentsExportReportsList } from './AssessmentsExportReportsList';
import { AssessmentsExportReportsSummary } from './AssessmentsExportReportsSummary';

interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  activityInstanceId: number;
  activityType: ActivityType;
  onDownloadReports?: typeof reportsApi.downloadReports;
}

type View =
  | 'picker'
  | 'whole-class-excel'
  | 'student-pdfs'
  | 'results-summary'
  | 'answer-key';

/*
A simplified version of ExportReports supporting only downloading complete results export. Used in Showbie Assessments
Updated: Extended to support Individual Student PDFs, Answer Key & Results Summary PDF exports
*/
export function AssessmentsExportReports({
  open,
  onClose,
  activityInstanceId,
  activityType,
  onDownloadReports = reportsApi.downloadReports,
}: Props) {
  const uiProps = {
    open,
    activityInstanceId,
    activityType,
    onClose,
    onDownloadReports,
  };

  return <AssessmentsExportReportsUI {...uiProps} />;
}

export function AssessmentsExportReportsUI({
  open,
  onClose,
  activityInstanceId,
  activityType,
  onDownloadReports,
}: Props) {
  const [view, setView] = React.useState<View>('picker');
  const [isExporting, setIsExporting] = React.useState(false);

  /* Reset to picker when dialog is closed or activity type changes
  Also reset exporting state when dialog closes */
  React.useEffect(() => {
    if (!open) setView('picker');
    if (!open) setIsExporting(false);
  }, [open]);
  React.useEffect(() => {
    setView('picker');
    setIsExporting(false);
  }, [activityType]);

  const reportSettings = React.useMemo(() => {
    return getReportSettings(activityType);
  }, [activityType]);

  const showBackBar = view !== 'picker' && !isExporting;

  return (
    <>
      <Dialog
        id="assessments-export-reports"
        size="default"
        open={open}
        title={translate('Export Results')}
        closeLabel={translate('Close')}
        onClose={onClose}
      >
        <div className="py-4">
          <Divider />
        </div>
        {showBackBar && (
          <div>
            <IconButton
              className="absolute flex items-center cursor-pointer border-none bg-white"
              onClick={() => setView('picker')}
              aria-label={translate('Back')}
              style={{ color: '#0099C7' }}
            >
              <IconChevron
                direction="left"
                title={translate('Back')}
                titleId="back-button"
              />
            </IconButton>
          </div>
        )}

        {view === 'picker' && (
          <AssessmentsExportReportsList
            onSelect={(key) => {
              if (key === 'whole-class-excel') setView('whole-class-excel');
              if (key === 'student-pdfs') setView('student-pdfs');
              if (key === 'results-summary') setView('results-summary');
              if (key === 'answer-key') setView('answer-key');
            }}
          />
        )}

        {view === 'whole-class-excel' && (
          <AssessmentsExportReportsCompleteResults
            key={activityInstanceId}
            activityInstanceId={activityInstanceId}
            initialOptions={reportSettings[ReportTypes.WholeClassExcel]}
            downloadReports={onDownloadReports}
            onExportingChange={setIsExporting}
          />
        )}

        {view === 'student-pdfs' && (
          <AssessmentsExportReportsIndividual
            key={activityInstanceId}
            activityInstanceId={activityInstanceId}
            initialOptions={reportSettings[ReportTypes.IndividualStudentPDF]}
            downloadReports={onDownloadReports}
            onExportingChange={setIsExporting}
          />
        )}
        {view === 'results-summary' && (
          <AssessmentsExportReportsSummary
            key={activityInstanceId}
            activityInstanceId={activityInstanceId}
            initialOptions={reportSettings[ReportTypes.QuestionSpecificPDF]}
            downloadReports={onDownloadReports}
            onExportingChange={setIsExporting}
          />
        )}

        {view === 'answer-key' && (
          <AssessmentsExportReportsAnswerKey
            key={activityInstanceId}
            activityInstanceId={activityInstanceId}
            initialOptions={reportSettings[ReportTypes.EmptyQuiz]}
            downloadReports={onDownloadReports}
            onExportingChange={setIsExporting}
          />
        )}
      </Dialog>
    </>
  );
}
